import React, { useEffect, useRef, useState } from "react";
import { sectionFrame } from "../../config/site";
import { programByTab, trackTabs, type TrackTab } from "../../content/site";
import { trackEvent } from "../../lib/analytics";
import { ProgramRow } from "../ui/ProgramRow";
import { SectionHeading } from "../ui/SectionHeading";
import { TabBar } from "../ui/TabBar";

type ProgramSectionProps = {
    activeTab: TrackTab;
    onChange: (tab: TrackTab) => void;
};

type ProgramEntry = (typeof programByTab)[TrackTab][number];

type GroupedProgramEntry =
    | ProgramEntry
    | {
          time: string;
          place?: string;
          floor?: string;
          talks: ProgramEntry[];
      };

const trackVenueByTab: Record<TrackTab, { place: string; floor: string }> = {
    "Войти в IT": { place: "Лекторий", floor: "3 этаж" },
    Development: { place: "Библиотека", floor: "2 этаж" },
    Management: { place: "Ивент", floor: "1 этаж" },
    Design: { place: "Библиотека", floor: "2 этаж" },
    Data: { place: "Песочница", floor: "4 этаж" },
    GameDev: { place: "Кибер-арена", floor: "2 этаж" },
};

function groupProgramItems(items: ProgramEntry[]): GroupedProgramEntry[] {
    const grouped: GroupedProgramEntry[] = [];

    for (const item of items) {
        if (item.break) {
            grouped.push(item);
            continue;
        }

        const previous = grouped[grouped.length - 1];
        const canMerge =
            previous &&
            "talks" in previous &&
            previous.time === item.time &&
            previous.place === item.place &&
            previous.floor === item.floor;

        if (canMerge) {
            previous.talks.push(item);
            continue;
        }

        grouped.push({
            time: item.time,
            place: item.place,
            floor: item.floor,
            talks: [item],
        });
    }

    return grouped;
}

export function ProgramSection({ activeTab, onChange }: ProgramSectionProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement | null>(null);
    const groupedProgram = groupProgramItems(programByTab[activeTab]);
    const activeVenue = trackVenueByTab[activeTab];

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!mobileMenuRef.current?.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    return (
        <section id="program" className="px-4 pt-[120px] sm:px-6 max-md:pt-[84px]">
            <div className={sectionFrame}>
                <SectionHeading label="Программа мероприятия" title="Узнай когда и где" />
                <div className="hidden max-lg:mb-6 max-lg:block">
                    <div ref={mobileMenuRef} className="relative w-full">
                        <button
                            type="button"
                            aria-haspopup="listbox"
                            aria-expanded={isMobileMenuOpen}
                            onClick={() => setIsMobileMenuOpen((current) => !current)}
                            className="flex h-[48px] w-full items-center justify-between rounded-[153.86px] border-[1.54px] border-accent bg-transparent px-6 py-3 text-[16px] leading-[1] font-normal tracking-[0] text-accent"
                            style={{
                                fontFamily:
                                    '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                            }}
                        >
                            <span>{activeTab}</span>
                            <span
                                className={[
                                    "text-accent transition-transform duration-200",
                                    isMobileMenuOpen ? "rotate-180" : "",
                                ].join(" ")}
                                aria-hidden="true"
                            >
                                <svg
                                    width="14"
                                    height="8"
                                    viewBox="0 0 14 8"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1 1L7 7L13 1"
                                        stroke="currentColor"
                                        strokeWidth="1.54"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </button>
                        {isMobileMenuOpen ? (
                            <div
                                role="listbox"
                                aria-label="Треки программы"
                                className="absolute left-0 top-[calc(100%+8px)] z-20 flex w-full flex-col overflow-hidden rounded-[24px] border-[1.54px] border-accent bg-black/95 p-2 shadow-[0_18px_48px_rgba(0,0,0,0.45)]"
                            >
                                {trackTabs.map((tab) => {
                                    const isActive = tab === activeTab;

                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            role="option"
                                            aria-selected={isActive}
                                            onClick={() => {
                                                trackEvent("program_track_select", {
                                                    tab,
                                                    source: "mobile_selector",
                                                });
                                                onChange(tab);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={[
                                                "flex min-h-[44px] items-center rounded-[18px] px-4 py-3 text-left text-[16px] leading-[1] font-normal tracking-[0] transition-colors",
                                                isActive ? "bg-accent text-black" : "text-white",
                                            ].join(" ")}
                                            style={{
                                                fontFamily:
                                                    '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                            }}
                                        >
                                            {tab}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="max-lg:hidden">
                    <TabBar
                        active={activeTab}
                        onChange={(tab) => {
                            trackEvent("program_track_select", { tab, source: "desktop_tabs" });
                            onChange(tab);
                        }}
                        variant="program"
                    />
                </div>
                <div className="mt-2 mb-6 max-lg:mt-3 max-lg:mb-5">
                    <div
                        className="text-[32px] font-bold leading-[1.1] tracking-[-0.04em] text-white max-lg:text-[24px]"
                        style={{
                            fontFamily:
                                '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        }}
                    >
                        {activeTab}
                    </div>
                    <div
                        className="mt-2 text-[22px] leading-[1.15] tracking-[-0.04em] text-muted max-lg:text-[16px]"
                        style={{
                            fontFamily:
                                '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        }}
                    >
                        {activeVenue.place}, {activeVenue.floor}
                    </div>
                </div>
                <div className="max-lg:pt-[36px]">
                    <div className="flex flex-col">
                        {groupedProgram.map((item, index) => (
                            <ProgramRow
                                key={`${item.time}-${"talks" in item ? item.talks.map((talk) => talk.topic).join("-") : item.topic}-${index}`}
                                {...item}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
